import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, LayoutGrid, Lock } from 'lucide-react';
import { CAPABILITIES_MODULE_ID, CAPABILITY_KEYS } from '../../../api/Navigation/moduleConfig';
import type { DemoSurfaceGrant } from '../../../api/Navigation/moduleConfigAdmin';
import {
  navigationModules,
  withoutPreviewPages,
  pageTabGroups,
  mobileModules,
} from '../../../components/layout/navigationModules';
import type { NavigationPage } from '../../../components/layout/types';
import { PageLayout } from '../../../components/ui/PageLayout';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import ChangeBar, { UnsavedChangesDialog, type ChangeItem, type ChangeItemKind } from './ChangeBar';
import type { ConfigItemModel, ConfigRowModel } from './ConfigSection';
import DriftPanel from './DriftPanel';
import FleetTable, { useSurfaceNamer, type FleetView } from './FleetTable';
import ScopeBar from './ScopeBar';
import SwitchTable, {
  DemoSurfacePanel,
  SwitchTableSkeleton,
  type DemoSurfaceRow,
  type SwitchRowExtras,
  type SwitchRowModel,
  type SwitchTableSection,
} from './SwitchTable';
import {
  HOME_MODULE_ID,
  HOME_PAGE_KEY,
  demoSurfaceId,
  pageKey,
  useCompanySummaries,
  useDemoSurfaceAccess,
  useModuleConfigEditor,
  type ConfigChange,
} from './useModuleConfigEditor';

/** Query-string key of the deep link: `/settings/module-config?company=<id>`. */
const COMPANY_PARAM = 'company';
/** Fleet view, shown while no tenant is open: `?view=surface&surface=<id>` (CO-862). */
const VIEW_PARAM = 'view';
const SURFACE_PARAM = 'surface';

const retryButtonClass =
  'shrink-0 rounded-lg border border-border bg-card px-3 py-1.5 font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

function LoadError({ message, onRetry, retryLabel }: { message: string; onRetry?: () => void; retryLabel: string }) {
  return (
    <div
      role="alert"
      data-testid="module-config-load-error"
      className="flex flex-wrap items-center gap-3 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
    >
      <AlertCircle aria-hidden="true" className="h-4 w-4 shrink-0" />
      <span className="min-w-0 flex-1">{message}</span>
      {onRetry && (
        <button type="button" onClick={onRetry} className={retryButtonClass}>
          {retryLabel}
        </button>
      )}
    </div>
  );
}

/** A demo row's switch mirrors the grant and is never editable, so its handler has nothing to do. */
const noToggle = () => undefined;

const changeKind = (change: ConfigChange): ChangeItemKind => {
  if (change.map === 'module') return 'module';
  return change.kind === 'mobile' ? 'mobilePage' : (change.kind as ChangeItemKind);
};

/**
 * Module Configuration page for Operators.
 * Per-module, per-page and per-TAB switches across the web app, the mobile app and the pages that own
 * a tab strip, plus the company capabilities. This file is composition only (CO-865): state, the load
 * gate and the delta save live in `useModuleConfigEditor` and `configDelta` (CO-860); the tenant
 * controls in `ScopeBar`, the switches in `SwitchTable`, the staged changes, their review and the
 * outcome of a save in `ChangeBar` (CO-864, CO-863).
 *
 * Demo surfaces (CO-849, CO-861) are not staged switches. A company's users see one by an active grant
 * alone, so the grant and revoke control is its own panel above the table (`DemoSurfacePanel`, state
 * in `useDemoSurfaceAccess`), applied at once, and the table's row for a demo key only mirrors the
 * grant. Module `home` and its page `/` are drawn locked ON (the server refuses to switch them off).
 *
 * With no tenant open the page is the fleet view (`FleetTable`, CO-862): which live tenants see which
 * demo surfaces. With a tenant open, `DriftPanel` lists the stored rows the table cannot show. Both
 * read for themselves; a remove or a tidy-up there changes the stored version, so the editor's
 * baseline is read again (`reloadKeepingChanges`) or its next save would answer 409.
 *
 * The selected tenant IS the `?company=<id>` query parameter, so a link opens on its tenant and the
 * browser's Back returns to the previous one. Leaving a tenant with staged changes is guarded on every
 * control of this page and on tab close or reload. It is NOT guarded on a sidebar click or the browser's
 * Back: the app runs on `BrowserRouter`, which has no navigation blocker.
 */
export default function ModuleConfig() {
  const { t } = useTranslation();

  const companyList = useCompanySummaries();
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get(COMPANY_PARAM);
  const selectedCompany =
    requested !== null && /^\d+$/.test(requested)
      ? companyList.companies.find(company => company.companyId === Number(requested)) ?? null
      : null;
  const selectedCompanyId = selectedCompany?.companyId ?? null;
  const listReady = !companyList.loading && companyList.failure === null;
  /** The link names a company the list does not hold: say so, never show an empty table for it. */
  const unknownCompany = listReady && requested !== null && selectedCompany === null;

  const editor = useModuleConfigEditor(selectedCompanyId);
  const { configStatus, configReady, isModuleEnabled, isPageEnabled, toggleModule, togglePage, changes } = editor;
  const { isModuleEnabledSaved, hasStoredRow } = editor;

  const demo = useDemoSurfaceAccess(selectedCompanyId);
  /** The demo read has an answer, good or bad. Until then the table is not drawn: a demo key would
   *  show as an ordinary, editable switch for as long as the list is unknown. */
  const demoSettled = demo.status === 'ready' || demo.status === 'failed';
  const surfaceNamer = useSurfaceNamer();
  /** Stale stored rows depend on the grants: the drift panel reads again when they change. */
  const grantsKey = demo.surfaces
    .filter(surface => surface.isGranted)
    .map(demoSurfaceId)
    .join(',');
  /**
   * Counts the accepted saves. The drift panel holds its own read of the stored rows and their
   * version, and the version covers every row: without a re-read after a save the panel's next
   * Remove is answered as changed elsewhere, and its lists keep the words from before the save.
   */
  const [savedEpoch, setSavedEpoch] = useState(0);
  useEffect(() => {
    if (editor.feedback?.type === 'success') setSavedEpoch(n => n + 1);
  }, [editor.feedback]);
  const fleetView: FleetView = searchParams.get(VIEW_PARAM) === 'surface' ? 'surface' : 'tenant';

  /** Where the operator asked to go while changes were staged; the prompt is open while this is set. */
  const [pendingTarget, setPendingTarget] = useState<{ companyId: number | null } | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);

  // The review lists the staged changes; any change to them (a toggle, a discard, a save) closes it,
  // so what was reviewed is always what Apply sends.
  useEffect(() => {
    setReviewOpen(false);
  }, [changes]);

  // Tab close and reload. In-app navigation is not covered, see the component comment.
  useEffect(() => {
    if (!editor.isDirty) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [editor.isDirty]);

  const goToCompany = useCallback(
    (companyId: number | null) => {
      setSearchParams(previous => {
        const next = new URLSearchParams(previous);
        if (companyId === null) next.delete(COMPANY_PARAM);
        else next.set(COMPANY_PARAM, String(companyId));
        return next;
      });
    },
    [setSearchParams]
  );

  const setFleetView = useCallback(
    (view: FleetView, surfaceId: string | null) => {
      setSearchParams(previous => {
        const next = new URLSearchParams(previous);
        if (view === 'surface') next.set(VIEW_PARAM, 'surface');
        else next.delete(VIEW_PARAM);
        if (view === 'surface' && surfaceId !== null) next.set(SURFACE_PARAM, surfaceId);
        else next.delete(SURFACE_PARAM);
        return next;
      });
    },
    [setSearchParams]
  );

  const requestCompany = (companyId: number | null) => {
    if (companyId === selectedCompanyId && !unknownCompany) return;
    if (editor.isDirty) {
      setPendingTarget({ companyId });
      return;
    }
    goToCompany(companyId);
  };

  /* ── Section models. Built per render from the registries and the editor's two maps. ── */

  const changedModules = new Set(changes.filter(change => change.map === 'module').map(change => change.key));
  const changedPages = new Set(changes.filter(change => change.map === 'page').map(change => change.key));
  /** Display name of every switch on the page, for the review list. Module ids and page keys cannot
   *  collide: a page key always holds a colon. */
  const names = new Map<string, { label: string; owner?: string }>();

  /* Demo keys of this company, as the server lists them. Matched case-insensitively, like the server:
     a page by its path under any module id, a tab by (tab group id, tab id), a module by its id. */
  const demoPages = new Map<string, DemoSurfaceGrant>();
  const demoTabs = new Map<string, DemoSurfaceGrant>();
  const demoModules = new Map<string, DemoSurfaceGrant>();
  demo.surfaces.forEach(surface => {
    if (surface.surfaceKind === 'page') demoPages.set(surface.surfaceKey.toLowerCase(), surface);
    else if (surface.surfaceKind === 'tab') demoTabs.set(pageKey(surface.moduleId, surface.surfaceKey).toLowerCase(), surface);
    else demoModules.set(surface.surfaceKey.toLowerCase(), surface);
  });
  /** What the registry knows of each demo surface, for the demo panel: filled while the rows are built. */
  const surfaceInfo = new Map<string, { label: string; owner?: string; hiddenBy?: string }>();

  const row = (model: ConfigRowModel, owner: string, extras: SwitchRowExtras = {}): SwitchRowModel => {
    names.set(model.key, { label: model.label, owner });
    return { ...model, ...extras, changed: changedPages.has(model.key) };
  };
  /** Row presence (CO-861): a value with no stored row is only the default. Words are given for the
   *  saved state only; a staged row already says "Changed". */
  const storedState = (key: string, checked: boolean): SwitchRowExtras => {
    const stored = hasStoredRow('page', key);
    if (changedPages.has(key)) return { stored };
    const word = checked ? (stored ? 'moduleConfig.state.onSet' : 'moduleConfig.state.onDefault') : 'moduleConfig.state.offSet';
    return { stored, stateLabel: t(word) };
  };
  /** The table row of a demo key: read-only, ON exactly when granted. The control is the demo panel. */
  const demoRow = (model: Pick<ConfigRowModel, 'key' | 'label' | 'path' | 'hiddenByModule' | 'status'>, owner: string, grant: DemoSurfaceGrant) =>
    row(
      {
        ...model,
        checked: grant.isGranted,
        disabled: true,
        onToggle: noToggle,
        description: t(grant.isGranted ? 'moduleConfig.demo.rowGranted' : 'moduleConfig.demo.rowNotGranted'),
      },
      owner,
      { demo: true }
    );
  /** The count line of a module with a note after it (the Home lock, or the demo state of a module). */
  const metaWithNote = (text: string, note: ReactNode): ReactNode => (
    <>
      {text}
      <span className="ms-2 inline-flex items-center gap-1 align-middle">{note}</span>
    </>
  );
  const enabledLabel = (rows: ConfigRowModel[]) =>
    t('moduleConfig.enabledCount', { on: rows.filter(r => r.checked).length, total: rows.length });
  const moduleToggle = (id: string, title: string, enabled: boolean, readOnly = false): ConfigItemModel['toggle'] => {
    names.set(id, { label: title });
    return {
      checked: enabled,
      disabled: !configReady || readOnly,
      onToggle: value => toggleModule(id, value),
      label: t('moduleConfig.a11y.enableModule', { name: title }),
    };
  };

  // UI-only demo previews (`previewOnly`) are gated by the DemoPreviews runtime flag, not page config,
  // so they get no per-company toggle here.
  const webItems: ConfigItemModel[] = withoutPreviewPages(navigationModules).map((module): ConfigItemModel => {
    const title = t(module.title);
    const homeModule = module.id === HOME_MODULE_ID;
    // A demo module is ON exactly when it is granted; its stored switch decides nothing.
    const moduleGrant = demoModules.get(module.id.toLowerCase());
    const enabled = moduleGrant ? moduleGrant.isGranted : homeModule || isModuleEnabled(module.id);
    /** What the tenant has now, whatever is staged: decides whether a granted demo page is reachable. */
    const savedOn = moduleGrant ? moduleGrant.isGranted : homeModule || isModuleEnabledSaved(module.id);
    if (moduleGrant) surfaceInfo.set(demoSurfaceId(moduleGrant), { label: title });

    const pageRow = (ownerId: string, page: NavigationPage): ConfigRowModel => {
      const key = pageKey(ownerId, page.path);
      const label = t(page.title);
      const status = page.comingSoon ? 'comingSoon' : undefined;
      const grant = demoPages.get(page.path.toLowerCase());
      if (grant) {
        surfaceInfo.set(demoSurfaceId(grant), { label, owner: title, hiddenBy: savedOn ? undefined : title });
        return demoRow({ key, label, path: page.path, hiddenByModule: !enabled, status }, title, grant);
      }
      const locked = key === HOME_PAGE_KEY;
      const checked = locked || isPageEnabled(ownerId, page.path);
      return row(
        {
          key,
          label,
          path: page.path,
          checked,
          // Under an OFF module the stored value is kept and shown, and cannot be edited.
          disabled: !configReady || !enabled || locked,
          hiddenByModule: !enabled,
          onToggle: value => togglePage(ownerId, page.path, value),
          status: locked ? 'locked' : status,
          hint: locked ? t('moduleConfig.homeLocked') : undefined,
        },
        title,
        locked ? {} : storedState(key, checked)
      );
    };
    const groups = [
      { id: module.id, rows: module.pages.map(page => pageRow(module.id, page)) },
      // Sub-module pages are keyed by the sub-module id, as they are stored.
      ...(module.subModules ?? []).map(sub => ({
        id: sub.id,
        heading: t(sub.title),
        rows: sub.pages.map(page => pageRow(sub.id, page)),
      })),
    ];
    const rows = groups.flatMap(group => group.rows);
    const countText = t('moduleConfig.count.pages', { count: rows.length });
    let meta: ReactNode = countText;
    if (homeModule) {
      meta = metaWithNote(
        countText,
        <>
          <Lock aria-hidden="true" className="h-3 w-3" />
          {t('Always enabled')}
        </>
      );
    } else if (moduleGrant) {
      meta = metaWithNote(
        countText,
        <>
          <StatusBadge variant="indigo" size="sm">
            {t('moduleConfig.badge.demo')}
          </StatusBadge>
          {t(moduleGrant.isGranted ? 'moduleConfig.demo.granted' : 'moduleConfig.demo.notGranted')}
        </>
      );
    }
    return {
      id: module.id,
      title,
      icon: module.icon,
      count: rows.length,
      meta,
      enabledLabel: enabledLabel(rows),
      changed: changedModules.has(module.id),
      toggle: moduleToggle(module.id, title, enabled, homeModule || moduleGrant !== undefined),
      groups,
    };
  });

  const mobileItems: ConfigItemModel[] = mobileModules.map((mobileModule): ConfigItemModel => {
    const enabled = isModuleEnabled(mobileModule.id);
    const title = t(mobileModule.title);
    const rows = mobileModule.pages.map((page): ConfigRowModel => {
      const locked = page.alwaysEnabled === true;
      const key = pageKey(mobileModule.id, page.id);
      const checked = locked ? true : isPageEnabled(mobileModule.id, page.id);
      return row(
        {
          key,
          label: t(page.title),
          path: page.id,
          checked,
          disabled: !configReady || !enabled || locked,
          hiddenByModule: !enabled,
          onToggle: value => togglePage(mobileModule.id, page.id, value),
          status: locked ? 'locked' : undefined,
        },
        title,
        locked ? {} : storedState(key, checked)
      );
    });
    return {
      id: mobileModule.id,
      title,
      icon: mobileModule.icon,
      count: rows.length,
      meta: t('moduleConfig.count.pages', { count: rows.length }),
      enabledLabel: enabledLabel(rows),
      changed: changedModules.has(mobileModule.id),
      toggle: moduleToggle(mobileModule.id, title, enabled),
      groups: [{ id: mobileModule.id, rows }],
    };
  });

  // Tabs INSIDE a page. Driven entirely by `pageTabGroups`; adding a group there adds an item here.
  const tabItems: ConfigItemModel[] = pageTabGroups.map((group): ConfigItemModel => {
    const title = t(group.title);
    const rows = group.tabs.map((tab): ConfigRowModel => {
      const locked = tab.alwaysEnabled === true;
      const key = pageKey(group.id, tab.id);
      const label = t(tab.title);
      const grant = demoTabs.get(key.toLowerCase());
      if (grant) {
        surfaceInfo.set(demoSurfaceId(grant), { label, owner: title });
        return demoRow({ key, label, path: tab.id }, title, grant);
      }
      const checked = locked ? true : isPageEnabled(group.id, tab.id);
      return row(
        {
          key,
          label,
          path: tab.id,
          checked,
          disabled: !configReady || locked,
          onToggle: value => togglePage(group.id, tab.id, value),
          status: locked ? 'locked' : undefined,
        },
        title,
        locked ? {} : storedState(key, checked)
      );
    });
    return {
      id: group.id,
      title,
      count: rows.length,
      // A route, so it reads left to right in every language.
      meta: <span dir="ltr" className="font-mono">{group.pagePath}</span>,
      enabledLabel: enabledLabel(rows),
      groups: [{ id: group.id, rows }],
    };
  });

  const capabilitiesTitle = t('moduleConfig.capabilities.title');
  const capabilityRows = CAPABILITY_KEYS.map((key): ConfigRowModel => {
    const rowKey = pageKey(CAPABILITIES_MODULE_ID, key);
    const checked = isPageEnabled(CAPABILITIES_MODULE_ID, key);
    return row(
      {
        key: rowKey,
        label: t(`moduleConfig.capabilities.${key}`),
        path: key,
        description: t(`moduleConfig.capabilities.${key}.screens`),
        hint: t(`moduleConfig.capabilities.${key}.effect`),
        checked,
        disabled: !configReady,
        onToggle: value => togglePage(CAPABILITIES_MODULE_ID, key, value),
      },
      capabilitiesTitle,
      storedState(rowKey, checked)
    );
  });
  const capabilityItems: ConfigItemModel[] = [
    {
      id: CAPABILITIES_MODULE_ID,
      title: capabilitiesTitle,
      icon: 'Lock',
      count: capabilityRows.length,
      meta: t('moduleConfig.count.dataTypes', { count: capabilityRows.length }),
      enabledLabel: enabledLabel(capabilityRows),
      intro: t('moduleConfig.capabilities.hint'),
      groups: [{ id: CAPABILITIES_MODULE_ID, rows: capabilityRows }],
    },
  ];

  const sections: SwitchTableSection[] = [
    { id: 'web', title: t('Web App'), icon: 'Monitor', items: webItems },
    { id: 'mobile', title: t('Mobile App'), icon: 'Smartphone', items: mobileItems },
    { id: 'tabs', title: t('Page Tabs'), icon: 'PanelTop', items: tabItems },
    { id: 'capabilities', title: t('moduleConfig.filter.capabilities'), icon: 'Lock', items: capabilityItems },
  ];

  /* The demo panel's rows: the server's list, named from the registry. A demo page the table does not
     list (a routed preview page has no switch) is still named from the registry; a key the registry
     does not hold at all is shown as its stored key. */
  const registryNames = new Map<string, { label: string; owner: string }>();
  navigationModules.forEach(module => {
    [...module.pages, ...(module.subModules ?? []).flatMap(sub => sub.pages)].forEach(page => {
      const path = page.path.toLowerCase();
      if (demoPages.has(path) && !registryNames.has(path)) {
        registryNames.set(path, { label: t(page.title), owner: t(module.title) });
      }
    });
  });
  const demoRows: DemoSurfaceRow[] = demo.surfaces.map(surface => {
    const id = demoSurfaceId(surface);
    const info =
      surfaceInfo.get(id) ??
      (surface.surfaceKind === 'page' ? registryNames.get(surface.surfaceKey.toLowerCase()) : undefined);
    return {
      id,
      surface: {
        surfaceKind: surface.surfaceKind,
        surfaceKey: surface.surfaceKey,
        // Read by the server for a tab only: its tab group id.
        ...(surface.surfaceKind === 'tab' ? { moduleId: surface.moduleId } : {}),
      },
      kind: surface.surfaceKind,
      label: info?.label ?? surface.surfaceKey,
      path: surface.surfaceKey,
      owner: info?.owner,
      granted: surface.isGranted,
      hiddenBy: surfaceInfo.get(id)?.hiddenBy,
    };
  });

  const changeItems: ChangeItem[] = changes.map(change => {
    const name = names.get(change.key);
    return {
      key: change.key,
      kind: changeKind(change),
      label: name?.label ?? change.key,
      owner: name?.owner,
      from: change.from,
      to: change.to,
      turnsModuleOff: change.map === 'module' && change.from && !change.to,
    };
  });

  /* ── Body. One state at a time: list loading, list failed, no tenant, config loading or failed, table. ── */

  let body: ReactNode;
  if (companyList.loading) {
    body = <SwitchTableSkeleton label={t('Loading...')} />;
  } else if (companyList.failure !== null) {
    // "No answer" is not "no companies". 401/403 gets no Retry because the answer will not change;
    // the status is the transport one, never the body's (CO-56).
    const refused = companyList.status === 401 || companyList.status === 403;
    body = (
      <LoadError
        message={refused ? t("You don't have permission to perform this action.") : t('Failed to load. Please try again.')}
        onRetry={refused ? undefined : companyList.reload}
        retryLabel={t('Retry')}
      />
    );
  } else if (companyList.companies.length === 0) {
    // A request that came back clean and carried nothing. States the fact and makes no claim about
    // who the reader is.
    body = <p className="py-12 text-center text-muted-foreground">{t('No companies found')}</p>;
  } else {
    const refusedConfig = editor.loadFailureStatus === 401 || editor.loadFailureStatus === 403;
    body = (
      <>
        <ScopeBar
          companies={companyList.companies}
          selectedId={selectedCompanyId}
          onRequestCompany={requestCompany}
          // Also while a grant or revoke is in flight: it must not resolve against another tenant.
          disabled={editor.saving || editor.reloading || demo.pendingId !== null}
        />

        {unknownCompany && (
          <p role="alert" data-testid="module-config-company-not-found" className="mb-6 rounded-lg border border-border bg-muted px-4 py-3 text-sm text-foreground">
            {t('moduleConfig.picker.notFound')}
          </p>
        )}
        {/* No tenant open: the fleet view. The picker above stays the way to one tenant by name. */}
        {selectedCompany === null && !unknownCompany && (
          <div data-testid="module-config-idle">
            <FleetTable
              view={fleetView}
              surfaceId={fleetView === 'surface' ? searchParams.get(SURFACE_PARAM) : null}
              onViewChange={setFleetView}
              onOpenCompany={requestCompany}
            />
          </div>
        )}

        {/* The table exists only for a config that loaded and belongs to the tenant on screen. While
            it loads or after it fails there is nothing to read as the tenant's state (CO-691, CO-864). */}
        {selectedCompany !== null && (!configReady || !demoSettled) && configStatus !== 'failed' && (
          <SwitchTableSkeleton label={t('moduleConfig.loadingConfig')} />
        )}
        {selectedCompany !== null && configStatus === 'failed' && (
          <LoadError
            message={refusedConfig ? t("You don't have permission to perform this action.") : t('moduleConfig.loadFailed')}
            onRetry={refusedConfig ? undefined : editor.retryLoad}
            retryLabel={t('Retry')}
          />
        )}
        {selectedCompany !== null && configReady && demoSettled && (
          <>
            {/* Keyed by company: a switch of tenant closes the list and any open confirmation. */}
            <DemoSurfacePanel
              key={`demo-${selectedCompany.companyId}`}
              companyName={selectedCompany.companyName}
              status={demo.status}
              refused={demo.loadFailureStatus === 401 || demo.loadFailureStatus === 403}
              rows={demoRows}
              tenantKind={demo.tenantKind}
              confirmGrant={demo.confirmGrant}
              pendingId={demo.pendingId}
              actionError={demo.actionError}
              disabled={editor.saving || editor.reloading}
              onGrant={demo.grant}
              onRevoke={demo.revoke}
              onDismissError={demo.dismissActionError}
              onRetry={demo.retryLoad}
            />
            {/* Keyed by company: a switch of tenant closes it and reads the next tenant's rows. */}
            <DriftPanel
              key={`drift-${selectedCompany.companyId}`}
              companyId={selectedCompany.companyId}
              companyName={selectedCompany.companyName}
              namer={surfaceNamer}
              refreshKey={`${grantsKey}#${savedEpoch}`}
              disabled={editor.saving || editor.reloading || demo.pendingId !== null}
              onStoredRowsChanged={editor.reloadKeepingChanges}
              onGrantsChanged={demo.retryLoad}
            />
            {/* Keyed by company: a switch of tenant resets the search and closes every item. */}
            <SwitchTable key={selectedCompany.companyId} sections={sections} />
            <ChangeBar
              companyName={selectedCompany.companyName}
              items={changeItems}
              saving={editor.saving}
              reloading={editor.reloading}
              feedback={editor.feedback}
              reviewOpen={reviewOpen}
              onReviewOpenChange={setReviewOpen}
              onDiscard={editor.reset}
              onApply={editor.save}
              onReload={editor.reloadKeepingChanges}
              onDismissFeedback={editor.dismissFeedback}
            />
          </>
        )}

        <UnsavedChangesDialog
          open={pendingTarget !== null}
          companyName={selectedCompany?.companyName ?? ''}
          onStay={() => setPendingTarget(null)}
          onDiscard={() => {
            const target = pendingTarget;
            setPendingTarget(null);
            // The switch itself drops the staged changes: the editor clears both maps before it
            // loads the next tenant.
            if (target) goToCompany(target.companyId);
          }}
          onReview={() => {
            setPendingTarget(null);
            setReviewOpen(true);
          }}
        />
      </>
    );
  }

  return (
    <PageLayout title={t('Module Configuration')} icon={LayoutGrid} accentColor="slate" maxWidth="5xl">
      {body}
    </PageLayout>
  );
}
